/**
 * "Compare packages" popup for the MRI scan section on the add-ons step.
 *
 * The section only offers Advanced and Pro, so a user who wants to know what
 * Core covers — or what the tiers actually differ on — has nowhere to look.
 * This is that reference, and it can also switch the selection.
 *
 * The matrix is a port of the marketing site's `CompareMriPackages`
 * (marketing-site/components/ui/compare-mri-packages.tsx, IMAGING_COMPARISON
 * and BLOOD_COMPARISON): same rows, same order, same per-tier true/false, and
 * the copy is the same `pricing.comp*` strings lifted into this app's locale
 * patch. Keep the two in sync — if the site adds a row, add it here. The
 * layout follows that page too: icon-led section bands, a row label with an ⓘ
 * that reveals the detail on demand rather than printing it inline (the
 * advanced-blood row's biomarker list alone is a paragraph), and filled discs
 * instead of bare ticks.
 *
 * Shell is the house modal pattern shared by ProductUspsModal and
 * MembershipDetailsModal: desktop = hand-rolled Radix overlay that scrolls
 * itself, mobile = IOSBottomSheet, switched on a JS breakpoint rather than a
 * media query so only one tree ever mounts.
 */
import { useEffect, useRef, useState } from "react";
import { X, Check } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useTranslation } from "react-i18next";
import { BookingLanguage } from "@/types/booking";
import { languageMap, BOOKING_NS } from "@/i18n";
import { useMarket } from "@/hooks/useMarket";
import { formatPrice, Product } from "../product-step/types";
import { InfoIcon } from "@/components/icons/InfoIcon";
import { PlusIcon } from "@/components/icons/PlusIcon";
import { CheckmarkIcon } from "@/components/icons/CheckmarkIcon";
import { PersonPackageIcon } from "@/components/icons/PersonPackageIcon";
import { BloodLabsIcon } from "@/components/icons/BloodLabsIcon";
import { IOSBottomSheet } from "../product-step/card/IOSBottomSheet";
import { useBreakpoint } from "../product-step/hooks/useBreakpoint";
import { typedSupabase } from "@/integrations/supabase/database-extensions";
import {
  comparisonColumns,
  comparisonSections,
  type ComparisonColumn,
  type ComparisonPayload,
} from "./comparisonColumns";

/**
 * Section icons by payload name. The `usp` payloads already store icons this
 * way (`"icon": "tube"`), and a component has to own the JSX either way — but
 * WHICH sections exist, and in what order, comes from the data.
 */
const SECTION_ICONS: Record<string, React.ReactNode> = {
  person_package: <PersonPackageIcon size={18} />,
  blood_labs: <BloodLabsIcon width={14} height={16} />,
};

/**
 * Column accents in catalog order, lifted from the marketing site's
 * PackageIconBox (mri-package-cards.tsx bgByVariant): green, blue, aubergine.
 *
 * By position, not by package: the modal never names a package. It cycles so a
 * fourth column still gets a disc instead of a bare icon.
 */
const ACCENTS = ["bg-light-green-200", "bg-light-blue-300", "bg-aubergine-300"];

type TFn = (key: string) => string;

/** Filled disc for included, muted dash for not. Hoisted: defined inside the
 *  modal these were a new component type on every render, so toggling one row's
 *  detail remounted the whole table. */
const Mark = ({ on, t }: { on: boolean; t: TFn }) =>
  on ? (
    <span className="inline-flex size-5 items-center justify-center rounded-full bg-deep-green-100 text-deep-green-700">
      <Check className="size-3.5" strokeWidth={3} aria-hidden />
      <span className="sr-only">{t("mriComparison.included")}</span>
    </span>
  ) : (
    <span className="inline-block text-neutral-200" aria-hidden>
      —<span className="sr-only">{t("mriComparison.notIncluded")}</span>
    </span>
  );

const Section = ({
  icon,
  heading,
  rows,
  columns,
  t,
  openInfo,
  setOpenInfo,
  detailMode = "inline",
  headerExtra,
}: {
  icon: React.ReactNode;
  heading: string;
  rows: string[];
  columns: ComparisonColumn[];
  t: TFn;
  openInfo: string | null;
  setOpenInfo: (slug: string | null) => void;
  /** Blood details are long (the biomarker list is a paragraph) — keep those
   *  behind an affordance. Imaging details are one line and read well inline. */
  detailMode?: "inline" | "toggle";
  /** Package columns, rendered level with the first section's heading. */
  headerExtra?: React.ReactNode;
}) => (
  <>
    {/* The only rule in the table. Row-level borders turned a 14-line matrix
        into a ledger; the section band is enough to group them. */}
    <tr className="border-b border-neutral-200">
      <th scope="colgroup" className="pb-2 pt-6 text-left align-bottom">
        <span className="flex items-center gap-2 text-neutral-500">
          {icon}
          <span className="text-xs font-semibold uppercase tracking-[0.6px]">
            {heading}
          </span>
        </span>
      </th>
      {headerExtra}
    </tr>
    {rows.map((slug, i) => {
      // Not every row has an explanatory line; i18next returns the key itself
      // when a key is absent, so compare against it.
      const infoKey = `mriComparison.row.${slug}Info`;
      const info = t(infoKey);
      const hasInfo = info !== infoKey;
      const expanded = openInfo === slug;
      const showToggle = hasInfo && detailMode === "toggle";
      const showInline = hasInfo && detailMode === "inline";
      return (
        // blue-gray-100 at 25% over white lands exactly on the page's own
        // background, so the banding reads as the page showing through
        // rather than as a new colour.
        <tr
          key={slug}
          className={i % 2 === 1 ? "bg-blue-gray-100/25" : undefined}
        >
          <td className="py-2 pr-3 align-middle">
            {/* Label and ⓘ share ONE inline flow rather than sitting in a flex
                row. Two reasons, both German: `hyphens-auto` + `break-words`
                give a 22-character compound ("Körperzusammensetzung") a place
                to break, so it wraps inside the 46%-wide label column instead
                of overflowing it — <html lang> is kept in sync per locale
                (useDocumentMeta), so the hyphenation dictionary is the right
                one. And with the icon as a plain inline sibling of the text,
                it reflows to just after the LAST word, wherever that word
                lands, instead of being pinned level with line one by
                `items-start`. */}
            <span className="hyphens-auto break-words text-base font-medium leading-snug text-neutral-900">
              {t(`mriComparison.row.${slug}`)}
              {showToggle && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenInfo(expanded ? null : slug);
                  }}
                  aria-expanded={expanded}
                  aria-label={t("mriComparison.showInfo")}
                  className="ms-1.5 inline-flex items-center justify-center align-middle normal-case"
                >
                  <InfoIcon size={15} />
                </button>
              )}
            </span>
            {(showInline || expanded) && (
              <p className="mt-0.5 pr-2 text-[13px] leading-snug text-neutral-500">
                {info}
              </p>
            )}
          </td>
          {columns.map((column) => (
            <td
              key={column.product.id}
              className="px-1 py-2 text-center align-middle"
            >
              <Mark on={column.included.has(slug)} t={t} />
            </td>
          ))}
        </tr>
      );
    })}
  </>
);

interface MriPackageComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: BookingLanguage;
  /** Full scan catalog — used to resolve each column's Select button. */
  scanProducts: Product[];
  /** Package id currently attached, so its column reads as chosen. */
  attachedScanId: string | null;
  onSelectScan: (packageId: string) => void;
}

export const MriPackageComparisonModal = ({
  isOpen,
  onClose,
  language,
  scanProducts,
  attachedScanId,
  onSelectScan,
}: MriPackageComparisonModalProps) => {
  const isMobile = useBreakpoint(640);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(BOOKING_NS, { lng: languageMap[language] });
  const { market } = useMarket();
  const [openInfo, setOpenInfo] = useState<string | null>(null);

  // Lock body scroll while open — the desktop overlay scrolls instead.
  // IOSBottomSheet does its own locking, so skip it on mobile.
  useEffect(() => {
    if (!isOpen || isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, isMobile]);

  const [payloadByProduct, setPayloadByProduct] = useState<Map<
    string,
    ComparisonPayload
  > | null>(null);

  // Lazily, on first open: the table is behind a button most users never press,
  // and this keeps the change out of get-products, whose response is cached for
  // 24h across every locale and market.
  useEffect(() => {
    if (!isOpen || payloadByProduct) return;
    void (async () => {
      const { data, error } = await typedSupabase
        .from("usp")
        .select("product_id, usp")
        .eq("use_case", "package_comparison")
        .in(
          "product_id",
          scanProducts.map((p) => p.id),
        );
      if (error) throw error;
      setPayloadByProduct(
        new Map(
          (data ?? []).map((row) => [
            row.product_id as string,
            row.usp as ComparisonPayload,
          ]),
        ),
      );
    })();
  }, [isOpen, payloadByProduct, scanProducts]);

  const columns = payloadByProduct
    ? comparisonColumns(scanProducts, payloadByProduct)
    : [];

  /* Just the tier names now — price and action moved to the pinned footer,
     so the header stays a label row and the table starts immediately. Sits in
     the IMAGING heading row: the section label only uses the left column. */
  const tierHeaders = columns.map(({ product }, i) => (
    <th key={product.id} scope="col" className="px-1 pb-2 pt-1 align-bottom">
      <span className="flex flex-col items-center justify-center gap-0.5 sm:flex-row sm:gap-1.5">
        {/* Disc + name side by side on desktop — stacked they made the header
            column taller than the section band it shares a row with. Under
            640px the columns are ~70px wide and "Advanced" beside a disc
            overflows into its neighbour, so there they stack. Same accent
            fills the product-step tab switcher uses, one step smaller. */}
        <span
          className={`flex size-6 shrink-0 items-center justify-center rounded-full ${
            ACCENTS[i % ACCENTS.length]
          }`}
        >
          <PersonPackageIcon size={16} />
        </span>
        <span className="text-xs font-semibold text-neutral-900 sm:text-sm">
          {t(`mriComparison.package.${product.human_readable_id}`)}
        </span>
      </span>
    </th>
  ));

  /* Price above, action below — one column per tier, aligned to the table's
     colgroup so each pair sits under the package it belongs to. The button
     itself stays a single short word: with the amount inside it wrapped to
     two lines in the narrow column. */
  const actionBar = (
    <div
      className="grid gap-1"
      // One column per package plus the label column. Not a Tailwind arbitrary
      // value: the count is data now, and JIT can't see a template literal.
      // On mobile the label column is dropped and the bar spans the full
      // width — kept, the remaining third leaves no room for "Auswählen" and
      // the last button hangs off the sheet.
      style={{
        gridTemplateColumns: isMobile
          ? `repeat(${columns.length}, 1fr)`
          : `34% repeat(${columns.length}, 1fr)`,
      }}
    >
      {!isMobile && <div />}
      {columns.map(({ product }) => {
        const isAttached = product.id === attachedScanId;
        const listAmount = product.default_price?.unit_amount ?? null;
        // Every package is quoted at its member price here, Core included: this
        // table is only reachable once a membership is in the cart, and that is
        // exactly what earns the discount. (The packages page is the surface
        // where Core's member price stays hidden — not this one.)
        const memberAmount = product.member_price?.unit_amount ?? listAmount;
        const currency = product.default_price?.currency || "CHF";
        const hasDiscount =
          listAmount != null &&
          memberAmount != null &&
          memberAmount < listAmount;
        return (
          <div
            key={product.id}
            data-testid={`mri-comparison-price-${product.human_readable_id}`}
            className="flex flex-col items-center gap-1"
          >
            {/* Mobile only: the tier names live in the table header, which is
                the first thing to scroll away on a 14-row matrix — leaving a
                pinned bar of three anonymous price/button pairs.

                Desktop is NOT immune — nothing pins that header row either, so
                a short laptop viewport scrolls it away the same way. Left to
                the mobile case here because that is what was reported and the
                desktop columns are wide enough to carry a name without a
                layout rethink; making the header row sticky is the better
                desktop fix and is deliberately not smuggled in. */}
            {isMobile && (
              <span className="text-[11px] font-semibold text-neutral-900">
                {t(`mriComparison.package.${product.human_readable_id}`)}
              </span>
            )}
            {memberAmount != null && (
              <span className="whitespace-nowrap text-[11px] text-neutral-600 sm:text-xs">
                {currency.toUpperCase()}{" "}
                {hasDiscount && (
                  <span
                    data-testid="mri-comparison-list-price"
                    className="text-neutral-400 line-through"
                  >
                    {formatPrice(listAmount, market, true)}
                  </span>
                )}{" "}
                <span
                  data-testid="mri-comparison-member-price"
                  className="font-semibold text-neutral-900"
                >
                  {formatPrice(memberAmount, market, true)}
                </span>
              </span>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelectScan(product.id);
                onClose();
              }}
              disabled={isAttached}
              className={`inline-flex h-10 w-full items-center justify-center gap-1 whitespace-nowrap rounded-full border px-1.5 text-[13px] font-medium normal-case transition-colors sm:gap-1.5 sm:px-2 sm:text-sm ${
                isAttached
                  ? "border-aubergine-800 bg-aubergine-800 text-white"
                  : "border-aubergine-800 text-aubergine-800 hover:bg-aubergine-50"
              }`}
            >
              {isAttached ? (
                <CheckmarkIcon className="size-4" />
              ) : (
                <PlusIcon size={15} strokeWidth={1.5} />
              )}
              {isAttached
                ? t("mriComparison.selected")
                : t("mriComparison.select")}
            </button>
          </div>
        );
      })}
    </div>
  );

  /* White pad so the buttons stay readable over the table, with a short fade
     above it so rows dissolve into the bar instead of being cut off. */
  const stickyActions = (
    <div className="sticky bottom-0 z-10">
      <div className="pointer-events-none h-8 bg-gradient-to-t from-background to-transparent" />
      <div className="bg-background px-4 pb-6 pt-1 sm:px-6">{actionBar}</div>
    </div>
  );

  const sections = comparisonSections(columns);

  const content = (
    <div className="px-4 sm:px-6">
      <h3 className="text-[22px] font-medium leading-7 text-neutral-900 sm:text-[28px] sm:leading-8">
        {t("mriComparison.title")}
      </h3>

      {columns.length > 0 && (
        <table className="w-full table-fixed border-collapse">
          <colgroup>
            {/* Narrow label column so three action columns fit without the
              table turning into a horizontal scroller. Wider on mobile: at
              34% of a phone the labels wrap one word per line. */}
            <col className="w-[46%] sm:w-[34%]" />
            {columns.map(({ product }) => (
              <col key={product.id} />
            ))}
          </colgroup>
          <tbody>
            {sections.map((section, i) => (
              <Section
                key={section.id}
                icon={section.icon ? SECTION_ICONS[section.icon] : null}
                heading={t(`mriComparison.section.${section.id}`)}
                rows={section.rows}
                columns={columns}
                t={t}
                openInfo={openInfo}
                setOpenInfo={setOpenInfo}
                // Inline details read well on desktop; on a phone each one
                // wraps to four lines and buries the matrix, so there every
                // section goes behind the ⓘ.
                detailMode={isMobile ? "toggle" : (section.detail ?? "inline")}
                // Package columns sit level with the first section's band.
                headerExtra={i === 0 ? tierHeaders : undefined}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <IOSBottomSheet isOpen={isOpen} onClose={onClose}>
        {/* Same handle as the desktop branch: the sheet is not a role="dialog",
            so a role-based selector only ever finds one of the two. */}
        <div
          data-testid="mri-comparison"
          ref={containerRef}
          className="relative flex h-full flex-col"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="absolute right-4 top-4 z-20 rounded-full p-1 text-neutral-400 hover:text-neutral-600"
            aria-label={t("bloodTracking.modal.close")}
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex-1 overflow-y-auto pb-16 pt-16">{content}</div>
          <div className="flex-shrink-0">
            {/* -mt-8 lifts the fade back OVER the scroll area. Without it the
                gradient takes its own 32px below the content, so it fades the
                empty gap rather than the rows, and the last row is cut hard at
                the scroll edge. Same three classes ProductUSPs uses for the
                same job (ProductUSPs.tsx:699); desktop needs none of it because
                `sticky bottom-0` already floats over the scrolling content. */}
            <div className="pointer-events-none relative z-10 -mt-14 h-14 bg-gradient-to-t from-white via-white/85 to-transparent" />
            <div className="bg-white px-4 pb-6 pt-1">{actionBar}</div>
          </div>
        </div>
      </IOSBottomSheet>
    );
  }

  if (!isOpen) return null;

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal forceMount>
        <div
          className="fixed inset-0 z-[60] overscroll-none bg-black/80"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <div className="flex min-h-full items-start justify-center px-4 py-8">
            <div
              className="relative w-full max-w-[800px]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Same spot as before, beside the card's top-right corner —
                  it just doesn't move now. The card scrolls inside itself
                  (max-h + own overflow) instead of the overlay scrolling the
                  whole card past the top of the screen. */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="absolute -right-12 top-1 z-10 hidden h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white transition-colors hover:bg-black/60 sm:flex"
                aria-label={t("bloodTracking.modal.close")}
              >
                <X className="h-5 w-5" />
              </button>
              <div
                data-testid="mri-comparison"
                role="dialog"
                aria-modal="true"
                aria-label={t("mriComparison.title")}
                ref={containerRef}
                className="max-h-[calc(100vh-4rem)] w-full overflow-y-auto rounded-3xl bg-background pt-10 shadow-lg"
              >
                {content}
                {stickyActions}
              </div>
            </div>
          </div>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
